import type { PriceCatalog } from '@snitch/contract'
import { buttonClass } from '@snitch/ui/button'
import { DAY_FORMS, DEVICE_FORMS, formatRub, plural } from '@snitch/ui/lib/format'
import { Brand } from '@snitch/ui/logo'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight, type LucideIcon, MessageCircle, PlugZap, Wallet } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { HomeSections, SubscriptionHero } from '../../../components/subscription-home.tsx'
import { SupportIconLink } from '../../../components/support-link.tsx'
import { ThemeToggle } from '../../../components/theme-toggle.tsx'
import { TrialSheet } from '../../../components/trial-sheet.tsx'
import { catalogQuery, heroOf, summaryQuery } from '../../../lib/billing.ts'
import { meQuery } from '../../../lib/session.ts'

export const Route = createFileRoute('/_authed/_shell/')({
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(catalogQuery),
      context.queryClient.ensureQueryData(summaryQuery),
    ]),
  component: Home,
})

/** Home: the trial offer without a subscription, the subscription hero with one. */
function Home() {
  const { data: catalog } = useSuspenseQuery(catalogQuery)
  const { data: summary } = useSuspenseQuery(summaryQuery)
  const { data: me } = useSuspenseQuery(meQuery)
  const hero = heroOf(summary)
  const firstName = me?.user.displayName.split(' ')[0] ?? ''

  if (!hero) return <Offer catalog={catalog} firstName={firstName} />

  return (
    <>
      <div className="flex flex-col pb-[130px] md:hidden">
        <SubscriptionHero hero={hero} catalog={catalog} />
        <div className="px-5 pt-7">
          <HomeSections summary={summary} />
        </div>
      </div>
      <main className="hidden flex-col gap-7 px-10 pt-8 pb-10 md:flex xl:px-12 xl:pt-9">
        <CabinetHeader firstName={firstName} overview />
        <div className="grid gap-7 xl:grid-cols-[minmax(0,1fr)_360px] xl:items-start">
          <div className="flex flex-col gap-9">
            <SubscriptionHero hero={hero} catalog={catalog} variant="card" />
            <HomeSections summary={summary} section="devices" />
          </div>
          <HomeSections summary={summary} section="details" />
        </div>
      </main>
    </>
  )
}

function CabinetHeader({ firstName, overview = false }: { firstName: string; overview?: boolean }) {
  return (
    <header className="flex items-center justify-between">
      <h1 className="font-bold font-display text-[30px] tracking-[-1px]">
        {overview ? 'Обзор' : `Привет${firstName ? `, ${firstName}` : ''}`}
      </h1>
      {overview ? <SupportIconLink className="bg-sunken" /> : <ThemeToggle />}
    </header>
  )
}

/**
 * "Signal / Главная · Нет подписки": the trial offer. Neutral page with the tab bar on phones,
 * a card in the cabinet.
 */
function Offer({ catalog, firstName }: { catalog: PriceCatalog; firstName: string }) {
  const [trialOpen, setTrialOpen] = useState(false)
  const { trial } = catalog
  const cheapest = Math.min(...catalog.deviceTiers.map((t) => t.monthly))
  const span = trial.days === 7 ? 'неделю' : `${trial.days} ${plural(trial.days, DAY_FORMS)}`
  const days = `${trial.days} ${plural(trial.days, DAY_FORMS)}`
  const price = `за ${formatRub(trial.price)}`
  const lead = `${trial.devices} ${plural(trial.devices, DEVICE_FORMS)} на ${span} — подключитесь и проверьте, как всё работает. Или сразу выберите обычный тариф.`
  const benefits = (
    <>
      <Benefit icon={PlugZap}>Подключение через HAPP или INCY</Benefit>
      <Benefit icon={MessageCircle}>Поддержка прямо в чате</Benefit>
      <Benefit icon={Wallet}>Обычные тарифы — от {formatRub(cheapest)} в месяц</Benefit>
    </>
  )
  const actions = (
    <>
      <button
        type="button"
        onClick={() => setTrialOpen(true)}
        className={buttonClass({ variant: 'ink', block: true, className: 'md:w-auto' })}
      >
        Попробовать за {formatRub(trial.price)}
        <ArrowRight aria-hidden="true" />
      </button>
      <Link
        to="/plans"
        className={buttonClass({
          variant: 'outline',
          size: 'md',
          block: true,
          className: 'md:h-14 md:w-auto',
        })}
      >
        Сразу выбрать тариф
      </Link>
    </>
  )
  const legal = (
    <>
      Нажимая, вы принимаете условия{' '}
      <a href="/legal/offer" className="underline underline-offset-2">
        оферты
      </a>
    </>
  )

  return (
    <>
      <main className="flex min-h-dvh flex-col justify-between gap-10 bg-bg px-5 pt-[max(16px,var(--tg-viewport-content-safe-area-inset-top,0px))] pb-[130px] text-ink md:hidden">
        <div className="flex flex-col gap-9">
          <div className="flex h-11 items-center justify-between">
            <Brand />
            <SupportIconLink className="bg-sunken" />
          </div>
          <section className="flex flex-col gap-1.5">
            <p className="font-semibold text-base text-ink-2">Тестовый тариф</p>
            <h1 className="whitespace-nowrap font-bold font-display text-[min(84px,21.5vw)] leading-none tracking-[-0.048em]">
              {days}
            </h1>
            <p className="font-bold font-display text-[34px] tracking-[-1.2px]">{price}</p>
            <p className="text-base leading-[1.45]">{lead}</p>
          </section>
          <ul className="flex flex-col gap-3.5">{benefits}</ul>
        </div>
        <div className="flex flex-col items-center gap-2.5">
          {actions}
          <p className="text-ink-2 text-xs">{legal}</p>
        </div>
      </main>

      <main className="hidden flex-col gap-7 px-10 pt-8 pb-10 md:flex xl:px-12 xl:pt-9">
        <CabinetHeader firstName={firstName} />
        <section className="flex flex-col gap-10 rounded-[22px] surface px-10 py-10 text-ink xl:flex-row xl:items-end xl:gap-12 xl:px-11">
          <div className="flex flex-1 flex-col gap-1.5">
            <p className="font-semibold text-base text-ink-2">Тестовый тариф</p>
            <p className="font-bold font-display text-[84px] leading-none tracking-[-4px]">
              {days}
            </p>
            <p className="font-bold font-display text-[34px] tracking-[-1.2px]">{price}</p>
            <p className="max-w-[560px] text-base leading-[1.45]">{lead}</p>
            <div className="flex flex-wrap gap-2.5 pt-[22px]">{actions}</div>
          </div>
          <ul className="flex flex-col gap-3.5 xl:w-[360px]">{benefits}</ul>
        </section>
        <p className="text-ink-3 text-xs">{legal}</p>
      </main>

      <TrialSheet open={trialOpen} onClose={() => setTrialOpen(false)} />
    </>
  )
}

function Benefit({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <li className="flex items-center gap-3">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-sunken">
        <Icon className="size-[18px]" aria-hidden="true" />
      </span>
      <span className="font-medium text-[15px]">{children}</span>
    </li>
  )
}
